import { CheckCircle, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

// ================================================================= //
//          GenerationProgress                                       //
// ================================================================= //
// Live stage list for scope generation, driven by real SSE `stage`  //
// events from /scope/generate/stream. Falls back gracefully when no //
// stage has arrived yet (shows the first as active).                //
// ================================================================= //

// Ordered pipeline stages with human labels. `wtr_substitution` only fires
// for water/flood damage; when skipped it simply shows as completed once a
// later stage arrives, which is accurate enough for a progress display.
const STAGES: Array<{ key: string; label: string }> = [
  { key: 'input_parser', label: 'Reading field notes' },
  { key: 'pattern_matcher', label: 'Matching known patterns' },
  { key: 'rules_engine', label: 'Applying scope rules' },
  { key: 'scope_builder', label: 'Building the scope with AI' },
  { key: 'wtr_substitution', label: 'Applying water-damage codes' },
  { key: 'per_item_rules', label: 'Applying per-item rules' },
  { key: 'completeness_check', label: 'Checking for missing items' },
  { key: 'post_build_rules', label: 'Checking companions & validations' },
  { key: 'validator', label: 'Validating codes & quantities' },
];

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

interface GenerationProgressProps {
  /** The stage key most recently reported by the stream, or null before any. */
  currentStage: string | null;
  /** Seconds since generation started, for the elapsed readout. */
  elapsedSeconds: number;
}

export function GenerationProgress({ currentStage, elapsedSeconds }: GenerationProgressProps) {
  const activeIndex = currentStage ? STAGES.findIndex((s) => s.key === currentStage) : 0;

  return (
    <div className="rounded-lg border bg-card p-8">
      <div className="flex flex-col items-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <h3 className="mt-4 text-lg font-semibold text-foreground">Generating scope</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          This can take up to a minute — the AI build step is the slow part. Elapsed{' '}
          {formatElapsed(elapsedSeconds)}.
        </p>

        <div className="mt-5 w-full max-w-sm space-y-2.5">
          {STAGES.map((stage, i) => {
            const done = i < activeIndex;
            const active = i === activeIndex;
            return (
              <div key={stage.key} className="flex items-center gap-3">
                {done ? (
                  <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600" />
                ) : active ? (
                  <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-primary" />
                ) : (
                  <div className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-slate-300" />
                )}
                <span
                  className={cn(
                    'text-sm',
                    done && 'text-foreground',
                    active && 'font-medium text-foreground',
                    !done && !active && 'text-muted-foreground',
                  )}
                >
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default GenerationProgress;
